/*
  Add all API paths used on the app here so that
  they are centrally documented.
*/
const API_ROUTES = {
  ME: '/api/sessions/me',
  ONBOARDING: '/api/onboarding',
  SUBSCRIPTION_TYPES: '/api/subscriptions',
  GET_CUSTOM_TEMPLATES: 'api/interview_templates',
  ADD_CUSTOM_TEMPLATES: 'api/interview_templates',
  MODIFY_CUSTOM_TEMPLATES: (templateId: number) =>
    `api/interview_templates/${templateId}`,
  SIGNUP: '/api/sessions/sign_up',
  SIGNUP_TEMPORARY_USER: '/api/sessions/sign_up_temporary_user',
  SIGNIN: '/api/sessions',
  SIGNOUT: '/api/sessions',
  STORE: '/api/store/products',
  STORE_CREATE_ORDER: '/api/store/orders',
  FORGOTPASSWORD: '/api/sessions/forgot_password',
  RESETPASSWORD: '/api/sessions/reset_password',
  CHANGEPASSWORD: '/api/sessions/me/password',
  GET_CONTRIBUTORS: 'api/sessions/me/contributors',
  VAULTS: '/api/vaults',
  VAULT_DETAIL: (id: number) => `/api/vaults/${id}`,
  VAULT_GUEST: (id: number) => `/api/vaults/${id}/show_with_token`,
  VAULT_EDIT: (id: number) => `/api/vaults/${id}`,
  // Public links: the numeric id of an old link, or the link's slug.
  VAULT_DETAIL_SHARED: (id: number | string) => `/api/vaults/${id}/share`,
  VAULT_DETAIL_VIDEO_CALL: (id: number, videoCallToken: string) =>
    `/api/vaults/${id}/view_video_call/${videoCallToken}`,
  VAULT_INFO_ONBOARDING: (id: number, token: string) =>
    `/api/vaults/${id}/pw_show_with_token/${token}`,
  DELETE_VAULT: (id: number) => `/api/vaults/${id}`,
  RESEND_VAULT_INVITATION: (invitationId: number) =>
    `/api/vault_invitations/${invitationId}/resend`,
  ADD_CONTRIBUTOR: (id: number) => `/api/vaults/${id}/contributors`,
  REMOVE_CONTRIBUTOR: (vaultId: number, invitationId: number) =>
    `/api/vaults/${vaultId}/contributors/${invitationId}`,
  LEAVE_VAULT: (vaultId: number) => `/api/vaults/${vaultId}/leave_vault`,
  CREATE_CLIP: (id: number) => `/api/vaults/${id}/clips`,
  CREATE_DOCUMENT: (id: number) => `/api/vaults/${id}/documents`,
  CREATE_CLIP_GUEST: (id: number) => `/api/vaults/${id}/add_clip_with_token`,
  CREATE_ALBUM_DOCUMENT_WITH_TOKEN: (albumId: number) =>
    `/api/albums/${albumId}/documents_with_token`,
  CREATE_REMINDER: (id: number) => `/api/vaults/${id}/reminders`,
  CREATE_TEMPORARY_USER: '/api/sessions/temporary_user',
  DESTROY_CLIPS: (id: number) => `/api/vaults/${id}/destroy_clips`,
  GET_DEFAULT_QUESTIONS: '/api/vaults/suggested_questions',
  EDIT_CLIP: (id: number) => `/api/clips/${id}`,
  EDIT_CLIP_GUEST: (id: number) => `/api/clips/${id}/update_with_token`,
  EDIT_REMINDER: (vaultId: number, reminderId: number) =>
    `api/vaults/${vaultId}/reminders/${reminderId}`,
  EDIT_ALBUM_METADATA: (id: number) => `/api/albums/${id}/update_metadata`,
  DELETE_CLIP: (id: number) => `/api/clips/${id}`,
  DELETE_REMINDER: (vaultId: number, reminderId: number) =>
    `/api/vaults/${vaultId}/reminders/${reminderId}`,
  REQUEST_VAULT_DOWNLOAD: (id: number) => `/api/vaults/${id}/request_download`,
  REQUEST_ALBUM_DOWNLOAD: (id: number) => `/api/albums/${id}/request_download`,
  PRESIGNED_URL: '/api/user/presigned_url',
  VERIFYEMAIL: '/api/sessions/verify_email',
  RESENDEMAIL: '/api/user/resend_verification',
  AGORA_OWNER_IN_CALL_STATUS: (videoCallToken: string) =>
    `/api/video_calls/${videoCallToken}/owner_in_call`,
  AGORA_GET_TOKEN: (videoCallToken: string) =>
    `/api/video_calls/${videoCallToken}/generate_token`,
  AGORA_START_RECORDING: (videoCallToken: string) =>
    `/api/video_calls/${videoCallToken}/start_recording`,
  AGORA_STOP_RECORDING: (videoCallToken: string) =>
    `/api/video_calls/${videoCallToken}/stop_recording`,
  AGORA_GET_VIDEO_CALL_USED_TIME: (id: number) =>
    `/api/vaults/${id}/video_call/ping`,
  SHARE_CLIP_BY_EMAIL: (clipId: number) => `/api/clips/${clipId}/email_share`,
  UPGRADE_VAULT: (id: number) => `/api/vaults/${id}/upgrade`,
  CANCEL_SUBSCRIPTION: (id: number) => `/api/vaults/${id}/cancel_subscription`,
  GET_SUBSCRIPTION_DEAL_BY_CODE: (code: string) =>
    `/api/subscription_deals/${code}`,
  APPLY_SUBSCRIPTION_DEAL_BY_CODE: '/api/subscription_deals',
  SUBSCRIPTION_PRICES: '/api/subscriptions/price',
  REMOVE_SUBSCRIPTION_DEL_CODE: (code: string) =>
    `/api/subscription_deals/${code}`,
  SEND_LINK_TO_VIDEO_CALL: (id: number) =>
    `/api/vaults/${id}/send_link_to_call`,
  ADD_PAYMENT_METHOD: '/api/user/add_payment_method',
  BILLING_PORTAL: '/api/user/billing_portal',
  GET_TIER_SWITCH_PRICES: (vaultId: number) =>
    `api/vaults/${vaultId}/tier_switch_prices`,
  GET_SHIPPING_COSTS: '/api/store/shipping_costs',
  INVITE_GUEST_TO_RECORD: (id: number) => `/api/vaults/${id}/invite_guest`,
  SEND_SAMPLE_EMAIL: (vaultID: number) =>
    `/api/vaults/${vaultID}/reminders/sample_reminder`,
  BUY_CLIPS: (id: number) => `/api/vaults/${id}/purchase_clips`,
  BUY_EXTRA_CALL_TIME: (vaultId: number) =>
    `/api/vaults/${vaultId}/purchase_videocall_time`,
  GET_PAYMENT: (id: number) => `/api/payments/${id}`,
  USER_ACTIVE: '/api/sessions/active',
  GET_COLLABORATORS: '/api/collaborators/vaults',
  REMOVE_GUEST: (id: number) => `/api/guests/${id}`,
  GET_OWNED_INVITES: '/api/collaborators/total_owned_invites',
  RESEND_PENDING_INVITES: '/api/collaborators/resend_pending_invites',
  GET_COLLABORATOR_AUDIT_DETAIL: '/api/collaborators/audit_detail',
  CREATE_ALBUM: (vaultId: number) => `/api/vaults/${vaultId}/albums`,
  ALBUM_DETAIL: (id: number) => `/api/albums/${id}`,
  ALBUM_DETAIL_SHARED: (id: number | string) => `/api/albums/${id}/share`,
  SHARED_WITH_ME: '/api/albums/shared_with_me',
  STORAGE_PLANS: '/api/storage_plans',
  VAULT_CHECKOUT_SESSION: (vaultId: number) =>
    `/api/vaults/${vaultId}/checkout_session`,
  ICLOUD_STREAM: '/api/icloud_shared_albums/stream',
  ICLOUD_ASSET_URLS: '/api/icloud_shared_albums/asset_urls',
  ICLOUD_DOWNLOAD: '/api/icloud_shared_albums/download',
  UPDATE_ALBUM: (id: number) => `/api/albums/${id}`,
  CONTENT_DETAIL: (id: number) => `/api/contents/${id}`,
  CONTENT_DETAIL_SHARED: (id: number | string) => `/api/contents/${id}/share`,
  SHARES: '/api/shares',
  SHARE: (id: string) => `/api/shares/${encodeURIComponent(id)}`,
  SHARE_RESEND: (id: string) => `/api/shares/${encodeURIComponent(id)}/resend`,
  SHARE_LINK: '/api/shares/link',
  INVITATION: (token: string) =>
    `/api/invitations/${encodeURIComponent(token)}`,
  INVITATION_ACCEPT: (token: string) =>
    `/api/invitations/${encodeURIComponent(token)}/accept`,
  INVITATION_DECLINE: (token: string) =>
    `/api/invitations/${encodeURIComponent(token)}/decline`,
  // A QR code rendered as a scannable SVG (public, usable as an <img> src).
  QR_CODE_IMAGE: (code: string) =>
    `/api/qr_codes/${encodeURIComponent(code)}/image`,
  ADD_JOURNAL_ENTRY: '/api/journal_documents',
  UPDATE_JOURNAL_ENTRY: (id: number) => `/api/journal_documents/${id}`,
  DOWNLOAD_JOURNAL_ENTRY_DOCX: (id: number) =>
    `/api/journal_documents/${id}/download_docx`,
  GET_ALBUM_LOCATIONS: (vaultId: number) =>
    `/api/vaults/${vaultId}/album_locations`,
  UPDATE_CONTENT_ORDER: '/api/contents/order',
  UPDATE_VAULT_MEMORY_ORDER: (vaultId: number) =>
    `/api/vaults/${vaultId}/memory_order`,
  UPDATE_ALBUM_ORDER: '/api/albums/order',
  GUEST_SELF_REGISTER: '/api/vaults/guest_self_register',
  ALBUM_GUEST_SELF_REGISTER: '/api/albums/guest_self_register',
  GUEST_INFO: '/api/guests/guest_info',
  // Album invite-by-email sharing
  INVITE_TO_ALBUM: (albumId: number) => `/api/albums/${albumId}/invite`,
  ALBUM_MEMBERS: (albumId: number) => `/api/albums/${albumId}/members`,
  ALBUM_COLLABORATOR_SUGGESTIONS: (albumId: number) =>
    `/api/albums/${albumId}/collaborator_suggestions`,
  ALBUM_INVITE_INFO: (token: string) => `/api/album_invitations/${token}`,
  ALBUM_INVITE_ACCEPT: (token: string) =>
    `/api/album_invitations/${token}/accept`,
  ALBUM_INVITE_DECLINE: (token: string) =>
    `/api/album_invitations/${token}/decline`,
};

export { API_ROUTES };
